import { Network } from '@yme/services/network';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { WISHLIST_ENDPOINT } from '../../endpoints';
import { WISHLIST_PENDING_STORAGE_KEY } from './constants';
class WishlistAPI {
    _items = [];
    pendingItems = new Set();
    get items() {
        return this._items;
    }
    set items(value) {
        if (!Array.isArray(value)) {
            return;
        }
        this._items = value;
        const result = this.normalize(value);
        this.onChange.forEach((el) => {
            el.current(result);
        });
    }
    clear() {
        if (this.items.length) {
            this.items = [];
        }
    }
    get pagePath() {
        return window.location.pathname;
    }
    async add(sku, pagePath) {
        try {
            this.pendingItems.add(sku);
            const result = await Network.post(WISHLIST_ENDPOINT, {
                sku,
                pagePath,
            });
            setDigitalData(DigitalDataMutation.ADD_TO_WISHLIST, {
                sku,
                pagePath,
            });
            this.items = result.data;
        }
        catch (error) {
            throw error;
        }
        finally {
            this.pendingItems.delete(sku);
        }
    }
    async remove(sku) {
        const itemToRemove = this.items.find((el) => el.sku === sku);
        if (!itemToRemove) {
            return;
        }
        try {
            this.pendingItems.add(sku);
            this.items = this.items.filter((el) => el.sku !== sku);
            await Network.delete(WISHLIST_ENDPOINT, {
                params: {
                    sku,
                    pagePath: itemToRemove.pathName || this.pagePath,
                },
            });
            setDigitalData(DigitalDataMutation.REMOVE_FROM_WISHLIST, {
                sku: itemToRemove.sku,
                pagePath: itemToRemove.pathName,
            });
        }
        catch (error) {
            this.items = [...this.items, itemToRemove];
            throw error;
        }
        finally {
            this.pendingItems.delete(sku);
            this.items = [...this.items];
        }
    }
    loadPromise = null;
    async load() {
        if (this.loadPromise) {
            return this.loadPromise;
        }
        this.loadPromise = new Promise(async (resolve) => {
            let isWishlistLoaded = false;
            try {
                const result = await Network.get(WISHLIST_ENDPOINT, {
                    params: {
                        pagePath: this.pagePath,
                    },
                });
                isWishlistLoaded = true;
                this.items = Array.isArray(result.data) ? result.data : [];
                await this.processDelayedItems();
            }
            catch (error) {
                // AEM returns 502 if no items in wishlist
                if (!isWishlistLoaded) {
                    try {
                        await this.processDelayedItems();
                    }
                    catch (err) {
                        this.items = [];
                    }
                }
                else {
                    this.items = [];
                }
            }
            resolve();
        });
        this.loadPromise.finally(() => {
            this.loadPromise = null;
        });
    }
    normalize(items = this._items) {
        return items
            .map((el) => {
            return {
                ...el,
                addedAt: new Date(el.addedAt).getTime(),
            };
        })
            .sort((a, b) => b.addedAt - a.addedAt);
    }
    onChange = [];
    get delayedItems() {
        try {
            const wishlistRawItems = localStorage.getItem(WISHLIST_PENDING_STORAGE_KEY);
            return wishlistRawItems ? JSON.parse(wishlistRawItems) : [];
        }
        catch (error) {
            return [];
        }
    }
    set delayedItems(value) {
        try {
            const dedupeValue = value.reduce((acc, el) => {
                if (!acc.find((item) => item.sku === el.sku)) {
                    acc.push(el);
                }
                return acc;
            }, []);
            localStorage.setItem(WISHLIST_PENDING_STORAGE_KEY, JSON.stringify(dedupeValue));
        }
        catch (error) {
            // EOL
        }
    }
    addDelayedItem(sku, pathName) {
        this.delayedItems = [
            ...this.delayedItems,
            {
                sku,
                pathName,
            },
        ];
    }
    async processDelayedItems() {
        // here we check local storage key for wishlist items, and if so, do async add;
        // this is needed to sync wishlist items between tabs
        const wishlistRawItems = this.delayedItems;
        if (!wishlistRawItems.length) {
            return;
        }
        this.delayedItems = [];
        for (const item of wishlistRawItems) {
            try {
                await this.add(item.sku, item.pathName);
            }
            catch (error) {
                this.addDelayedItem(item.sku, item.pathName);
            }
        }
    }
}
export const API = new WishlistAPI();
